// Report del servizio fiduciario, per turno. Il titolare vede le foto start/stop,
// regola le ore pagate al collaboratore e approva la relazione giornaliera
// (ore extra, km, spese), come in TabReport per gli incarichi.
// Differenze rispetto a TabReport:
// - la relazione può mancare (turno solo start/stop): si mostrano foto e ore;
// - le foto start/stop stanno sul turno, non sulla relazione.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Clock, CheckCircle2, XCircle, Receipt, FileText, ChevronDown, ChevronUp,
    Loader2, MapPin, Camera, CreditCard, Car, ShieldCheck,
    AlertTriangle, Calendar, ToggleLeft, ToggleRight, ChevronLeft, ChevronRight,
    ExternalLink,
} from 'lucide-react';
import { useToast } from './ToastContext';
import { Modal } from './Modal';
import {
    RelazioneGiornalieraService, ServizioFiduciarioService,
} from '../services/apiService';
import NumberInput from './NumberInput';
import {
    ApprovazioniFilters, ApprovazioneFiltersValue, EMPTY_APPROVAZIONE_FILTERS,
    passesApprovazioneFilters, ritardoMinutiTurno, totaleSpeseReport,
    BulkApproveModal,
} from './ApprovazioniFilters';
import { BuonoPastoAutoCard, oreEffettiveTurnoForBuonoPasto } from './BuonoPastoAutoCard';
import { collassaSessioniDegenerate } from '../utils/reportDedup';
import AutoAziendaleSelector from './AutoAziendaleSelector';

interface Props {
    servizioId: number;
    turni: any[];
    tariffaOrariaCliente: number;
    // Prezzo cliente: ORARIA (ore × tariffa, default) o FORFAIT (prezzo fisso di contratto).
    // A forfait il ricavo per turno non ha senso e non viene mostrato.
    tipoTariffaCliente?: 'ORARIA' | 'FORFAIT' | string;
    prezzoForfaitCliente?: number;
    tariffeCollaboratori: any[];
    onTurniRefresh?: () => void | Promise<void>;
}

const oreFra = (oraStart?: string, oraEnd?: string): number => {
    if (!oraStart || !oraEnd) return 0;
    const parse = (s: string) => {
        const [h, m] = s.split(':').map(Number);
        return (h || 0) * 60 + (m || 0);
    };
    let diff = parse(oraEnd) - parse(oraStart);
    if (diff < 0) diff += 24 * 60;
    return Math.round((diff / 60) * 100) / 100;
};


/** Lunedì della settimana ISO della data fornita (clone, no mutazione input). */
const lunediDella = (d: Date): Date => {
    const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const dow = out.getDay(); // 0=Dom..6=Sab
    const diff = dow === 0 ? -6 : 1 - dow; // riporta a lunedì
    out.setDate(out.getDate() + diff);
    return out;
};
const isoDate = (d: Date): string => {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const addDays = (d: Date, n: number): Date => {
    const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    out.setDate(out.getDate() + n);
    return out;
};
const formatGiornoMese = (d: Date): string =>
    d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });

/**
 * Converte ore decimali in stringa leggibile "X ore e Y min". Esempi:
 *   0     → "—"   (oppure "0 min" se zero=true)
 *   0.25  → "15 min"
 *   1     → "1 ora"
 *   1.5   → "1 ora e 30 min"
 *   2.5   → "2 ore e 30 min"
 * Arrotonda al minuto più vicino: 1.1 (=66 min) → "1 ora e 6 min".
 */
// Ore in formato decimale (es. "1.5h"), come in Investigazioni.
const formatOreMinuti = (ore: number | null | undefined, opts: { zero?: 'dash' | 'show' } = { zero: 'dash' }): string => {
    const n = Number(ore) || 0;
    if (n <= 0) return opts.zero === 'show' ? '0h' : '—';
    return `${Math.round(n * 100) / 100}h`;
};

/** Decompone ore decimali in {ore intere, minuti 0-59} arrotondando al minuto. */
const hToHM = (ore: number | null | undefined): { h: number; m: number } => {
    const totMin = Math.max(0, Math.round((Number(ore) || 0) * 60));
    return { h: Math.floor(totMin / 60), m: totMin % 60 };
};

/** Ricompone ore decimali da {ore, minuti}. */
const hmToHours = (h: number, m: number): number => {
    const totMin = Math.max(0, (Number(h) || 0) * 60 + (Number(m) || 0));
    return Math.round(totMin / 60 * 100) / 100;
};
const oreFraDate = (start?: string, end?: string): number => {
    if (!start || !end) return 0;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (isNaN(s) || isNaN(e) || e <= s) return 0;
    return Math.round(((e - s) / 3_600_000) * 100) / 100;
};

// ─── Badge "Turno in range" (stessa logica di ApprovazioneSpeseView) ──
// True se, senza override del titolare, l'inizio o la fine timbrati cadono nella
// finestra di tolleranza del turno: in approvazione vale allora l'orario previsto.
const _hhmmFromIso = (iso?: string): string | null => {
    if (!iso) return null;
    const m = String(iso).match(/T(\d{2}:\d{2})/);
    return m ? m[1] : null;
};
const _minTod = (hhmm?: string | null): number | null => {
    if (!hhmm) return null;
    const [h, m] = String(hhmm).split(':').map(Number);
    return (isNaN(h) || isNaN(m)) ? null : h * 60 + m;
};
const _inWindow = (t?: string | null, da?: string | null, a?: string | null): boolean => {
    const tm = _minTod(t), dm = _minTod(da), am = _minTod(a);
    if (tm == null || dm == null || am == null) return false;
    return dm <= am ? (tm >= dm && tm <= am) : (tm >= dm || tm <= am); // else = finestra a cavallo mezzanotte
};
const turnoInRange = (t: any): boolean => {
    if (!t || t.oreCollaboratoreOverride != null) return false; // override esplicito → legacy
    const aStart = _hhmmFromIso(t.effettivoInizio);
    const aEnd = _hhmmFromIso(t.effettivoFine);
    if (!aStart || !aEnd) return false; // timbrature incomplete
    const hasRi = !!(t.rangeInizioDa && t.rangeInizioA);
    const hasRf = !!(t.rangeFineDa && t.rangeFineA);
    if (!hasRi && !hasRf) return false; // nessun range impostato
    const startSnapped = hasRi && _inWindow(aStart, t.rangeInizioDa, t.rangeInizioA);
    const endSnapped = hasRf && _inWindow(aEnd, t.rangeFineDa, t.rangeFineA);
    return !!(startSnapped || endSnapped);
};

export const TabReportServizio: React.FC<Props> = ({
    servizioId, turni, tariffaOrariaCliente,
    tipoTariffaCliente, prezzoForfaitCliente,
    tariffeCollaboratori, onTurniRefresh,
}) => {
    const isForfait = tipoTariffaCliente === 'FORFAIT';
    const { addToast } = useToast();
    // Settimana lun-dom: si salva il lunedì, la domenica è +6 giorni.
    const [weekStart, setWeekStart] = useState<Date>(() => lunediDella(new Date()));
    const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
    const dataDal = useMemo(() => isoDate(weekStart), [weekStart]);
    const dataAl  = useMemo(() => isoDate(weekEnd),   [weekEnd]);
    const [reports, setReports] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState<number | null>(null);

    // Approvazione/annullamento relazione giornaliera
    const [savingRelId, setSavingRelId] = useState<number | null>(null);
    const [annullandoRelId, setAnnullandoRelId] = useState<number | null>(null);

    // Editing inline della relazione espansa (ore extra, km, spese approvate)
    const [editReport, setEditReport] = useState<Record<number, any>>({});

    // Preview foto
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewLabel, setPreviewLabel] = useState<string>('');

    // ─── Carica relazioni del servizio per la settimana selezionata ─────────
    const fetchReports = useCallback(() => {
        if (!servizioId) return;
        setLoading(true);
        RelazioneGiornalieraService.getByServizio(servizioId, { dataDal, dataAl })
            .then((res: any) => {
                const data: any[] = Array.isArray(res) ? res : (res?.data || []);
                setReports(data);
                // Precompila editReport con i valori correnti per la modifica inline.
                const editMap: Record<number, any> = {};
                data.forEach(r => {
                    editMap[r.id] = {
                        // Le ore extra non si modificano dalla card (si rettifica da "Ore pagate"):
                        // per orePagate = GPS + extra si usano le ore dichiarate dal collaboratore.
                        oreExtraApprovate: r.oreExtra ?? 0,
                        kmApprovati: r.kmApprovati ?? r.kmPercorsi ?? 0,
                        autoAziendale: !!r.autoAziendale,
                        // Le ore pagate non si inizializzano qui: il default (GPS + extra o override)
                        // dipende dal turno ed è calcolato al render quando il valore è undefined.
                        oreCollab: undefined as number | undefined, // un campo decimale (es. 1.5), come in Investigazioni
                        spese: (r.speseEspanso || r.spese || []).map((s: any) => ({
                            ...s,
                            importoApprovato: s.importoApprovato ?? (s.anticipatoAzienda ? 0 : s.importo),
                        })),
                    };
                });
                setEditReport(editMap);
            })
            .catch(() => addToast('Errore caricamento relazioni', 'error'))
            .finally(() => setLoading(false));
    }, [servizioId, dataDal, dataAl, addToast]);

    useEffect(() => {
        fetchReports();
        // Ricarica le relazioni al ritorno del focus, così le approvazioni fatte
        // in un'altra scheda si vedono senza smontare il componente.
        const onFocus = () => fetchReports();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [fetchReports]);

    const reportByTurno = useMemo(() => {
        const m: Record<number, any> = {};
        reports.forEach(r => { if (r.turnoId) m[r.turnoId] = r; });
        return m;
    }, [reports]);

    const tariffaCollabMap = useMemo(() => {
        const m: Record<number, number> = {};
        (tariffeCollaboratori || []).forEach((t: any) => {
            if (t.userId != null) m[t.userId] = Number(t.tariffaOraria) || 0;
        });
        return m;
    }, [tariffeCollaboratori]);

    // Filtri combinabili condivisi con le altre viste approvazioni.
    const [filters, setFilters] = useState<ApprovazioneFiltersValue>(EMPTY_APPROVAZIONE_FILTERS);
    // Approvazione massiva: modale di conferma e loader.
    const [bulkApproveOpen, setBulkApproveOpen] = useState(false);
    const [savingBulk, setSavingBulk] = useState(false);
    // Selezione multipla per approvare più turni senza aprire ogni card.
    // Si lavora per rel.id, poi si risale alla coppia {turno, rel}.
    const [selectedRelIds, setSelectedRelIds] = useState<Set<number>>(new Set());

    // Buono pasto per relazione, default true. Se false il backend non crea
    // la spesa anche quando la soglia ore è superata.
    const [applicaBpByRel, setApplicaBpByRel] = useState<Record<number, boolean>>({});
    const isBpAttivo = (relId: number) =>
        applicaBpByRel[relId] !== undefined ? applicaBpByRel[relId] : true;

    const turniDelMeseTutti = useMemo(() => {
        return (turni || [])
            .filter(t => t.dataTurno && t.dataTurno >= dataDal && t.dataTurno <= dataAl)
            .filter(t => t.statoAccettazione !== 'RIFIUTATO' && !t.dataRevoca)
            .sort((a, b) => (a.dataTurno || '').localeCompare(b.dataTurno || ''));
    }, [turni, dataDal, dataAl]);

    // Stato del turno per il filtro:
    // - APPROVATI: relazione APPROVATO (o, per turni vecchi senza relazione, oreApprovate=true);
    // - DA_APPROVARE: turno svolto/in corso con relazione non approvata o mancante;
    // - PIANIFICATI: turno non ancora avviato.
    const statoDelTurno = useCallback((t: any): 'PIANIFICATI' | 'DA_APPROVARE' | 'APPROVATI' => {
        const rel = reportByTurno[t.id];
        const relApprovata = rel && rel.stato === 'APPROVATO';
        if (relApprovata) return 'APPROVATI';
        if (!rel && t.oreApprovate) return 'APPROVATI';
        const isSvolto = !!t.effettivoInizio;
        if (isSvolto) return 'DA_APPROVARE';
        return 'PIANIFICATI';
    }, [reportByTurno]);

    const turniDelMese = useMemo(() => {
        return turniDelMeseTutti.filter((t: any) => {
            const rel = reportByTurno[t.id];
            // Per i pianificati km e spese non sono noti: undefined li esclude dai filtri
            // su km/spese. Con una relazione si passa 0 come valore reale.
            const km    = rel ? Number(rel.kmApprovati ?? rel.kmPercorsi ?? 0) : undefined;
            const spese = rel ? totaleSpeseReport(rel.speseEspanso || rel.spese) : undefined;
            const ritardoMin = ritardoMinutiTurno(t);
            const stato = statoDelTurno(t);
            return passesApprovazioneFilters(filters, { ritardoMin, km, spese, stato });
        });
    }, [turniDelMeseTutti, filters, reportByTurno, statoDelTurno]);

    // ─── Approva / annulla relazione giornaliera ────────────────────────────
    const setRelField = (id: number, field: string, value: any) =>
        setEditReport(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
    const setRelSpesa = (rid: number, sid: number, v: any, campo: string = 'importoApprovato') =>
        setEditReport(prev => ({
            ...prev,
            [rid]: {
                ...prev[rid],
                spese: prev[rid].spese.map((s: any) => s.id === sid ? { ...s, [campo]: v } : s),
            },
        }));
    // Il titolare può correggere il flag "Carta az." dichiarato dal collaboratore,
    // come in ApprovazioneSpeseView. Va riallineato anche l'importo approvato: su carta
    // aziendale è 0 e, tolto il flag, resterebbe 0 pur essendo rimborsabile.
    const toggleSpesaAnticipata = (rid: number, sid: number) =>
        setEditReport(prev => ({
            ...prev,
            [rid]: {
                ...prev[rid],
                spese: prev[rid].spese.map((s: any) => {
                    if (s.id !== sid) return s;
                    const anticipata = !s.anticipatoAzienda;
                    return {
                        ...s,
                        anticipatoAzienda: anticipata,
                        importoApprovato: anticipata
                            ? 0
                            : (Number(s.importoApprovato) > 0 ? s.importoApprovato : s.importo),
                    };
                }),
            },
        }));

    const approvaRelazione = async (rel: any, turno: any) => {
        if (!window.confirm('Approvi la relazione e invii a contabilità?')) return;
        const ed = editReport[rel.id] || {};
        setSavingRelId(rel.id);
        try {
            // "Ore pagate": si salva sempre l'override col valore mostrato, perché è quello che
            // il titolare approva. Il default è lo stesso dell'input (override o GPS + extra).
            {
                const oreGps = oreFraDate(turno.effettivoInizio, turno.effettivoFine);
                const oreExtraApprovate = Number(rel.oreExtra) || 0;
                const def = turno.oreCollaboratoreOverride != null
                    ? Number(turno.oreCollaboratoreOverride)
                    : oreGps + oreExtraApprovate;
                const ore = ed.oreCollab != null ? Number(ed.oreCollab) : def;
                if (!isNaN(ore) && ore >= 0) {
                    await ServizioFiduciarioService.setOreCollaboratore(turno.id, ore);
                }
            }
            await RelazioneGiornalieraService.updateRevisione(rel.id, {
                // Le ore extra si approvano come dichiarate; le rettifiche passano da "Ore pagate".
                oreExtraApprovate: Number(rel.oreExtra) || 0,
                kmApprovati: Number(ed.kmApprovati) || 0,
                // I turni hanno ore pianificate fisse, quindi non c'è un oreFatturabili distinto.
                oreFatturabiliApprovate: 0,
                autoAziendale: !!ed.autoAziendale,
                // Toggle buono pasto del titolare per questo singolo report.
                applicaBuonoPasto: isBpAttivo(rel.id),
                speseApprovate: (ed.spese || []).map((s: any) => ({
                    id: s.id,
                    anticipatoAzienda: !!s.anticipatoAzienda,
                    importoApprovato: s.anticipatoAzienda ? 0 : (Number(s.importoApprovato) || 0),
                    importoCarta: s.anticipatoAzienda ? (Number(s.importoCarta ?? s.importo) || 0) : null,
                })),
            });
            addToast('Relazione approvata', 'success');
            // Ricarica dal BE i valori salvati invece di stimarli lato client. Si attendono
            // entrambi i refresh prima di togliere "saving", altrimenti per qualche frame
            // si vedrebbe il vecchio override.
            await Promise.all([fetchReports(), Promise.resolve(onTurniRefresh?.())]);
        } catch { addToast('Errore approvazione', 'error'); }
        finally { setSavingRelId(null); }
    };

    // Approvazione massiva lato server dei report visibili con i valori del collaboratore;
    // per i turni il backend imposta anche oreApprovate. Le modifiche inline non salvate
    // si perdono (il modale lo segnala).
    const approveBulkFiduciario = async (
        coppie: Array<{ turno: any; rel: any }>
    ) => {
        setSavingBulk(true);
        try {
            const ids = coppie.map(c => c.rel.id);
            const res = await RelazioneGiornalieraService.bulkApprove(ids);
            const approvati = new Set(res.approvati || []);
            const okCount = approvati.size;
            setSelectedRelIds(prev => {
                const s = new Set(prev);
                approvati.forEach(id => s.delete(id));
                return s;
            });
            // Ricarica dal BE i valori riapplicati.
            await fetchReports();
            const skipCount = (res.saltati || []).length;
            addToast(
                skipCount > 0
                    ? `Approvati ${okCount} report (${skipCount} saltati)`
                    : `Approvati ${okCount} report`,
                'success',
            );
            onTurniRefresh?.();
        } catch {
            addToast('Errore approvazione in blocco', 'error');
        } finally {
            setSavingBulk(false);
            setBulkApproveOpen(false);
        }
    };

    const annullaRelazione = async (rel: any) => {
        if (!window.confirm('Annullare l\'approvazione? La relazione torna "Da approvare".')) return;
        setAnnullandoRelId(rel.id);
        try {
            await RelazioneGiornalieraService.annullaApprovazione(rel.id);
            addToast('Approvazione annullata', 'success');
            setReports(prev => prev.map(r => r.id === rel.id ? { ...r, stato: 'DA_REVISIONARE' } : r));
            onTurniRefresh?.();
        } catch { addToast('Errore annullamento', 'error'); }
        finally { setAnnullandoRelId(null); }
    };

    const openPreview = (url: string, label: string) => {
        setPreviewUrl(url); setPreviewLabel(label);
    };

    // Coppie {turno, rel} visibili con relazione DA_REVISIONARE, per "Approva tutti".
    const turniConRelazionePending = useMemo(() => {
        const out: Array<{ turno: any; rel: any }> = [];
        for (const t of turniDelMese) {
            const rel = reportByTurno[t.id];
            if (rel && (!rel.stato || rel.stato === 'DA_REVISIONARE')) {
                out.push({ turno: t, rel });
            }
        }
        return out;
    }, [turniDelMese, reportByTurno]);

    // ─── Render ────────────────────────────────────────────────────────────
    return (
        <div className="space-y-4">
            {/* Selettore settimanale (lun-dom) + intestazione */}
            <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-slate-200 shadow-sm gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Calendar size={18} className="text-indigo-600" />
                    <h2 className="font-bold text-slate-700">Report Turni</h2>
                </div>
                <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl p-1">
                    <button
                        type="button"
                        onClick={() => setWeekStart(prev => addDays(prev, -7))}
                        className="p-1.5 rounded-lg hover:bg-white text-slate-500 hover:text-indigo-600 transition-colors"
                        aria-label="Settimana precedente"
                        title="Settimana precedente"
                    >
                        <ChevronLeft size={16}/>
                    </button>
                    {/* Click sull'etichetta: torna alla settimana corrente. */}
                    <button
                        type="button"
                        onClick={() => setWeekStart(lunediDella(new Date()))}
                        className="px-2 py-1 text-xs font-bold text-slate-700 hover:text-indigo-700 hover:bg-white rounded transition-colors whitespace-nowrap"
                        title="Torna alla settimana corrente"
                    >
                        Lun {formatGiornoMese(weekStart)} – Dom {formatGiornoMese(weekEnd)}{' '}
                        <span className="text-slate-400 font-normal">({weekStart.getFullYear()})</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setWeekStart(prev => addDays(prev, 7))}
                        className="p-1.5 rounded-lg hover:bg-white text-slate-500 hover:text-indigo-600 transition-colors"
                        aria-label="Settimana successiva"
                        title="Settimana successiva"
                    >
                        <ChevronRight size={16}/>
                    </button>
                </div>
            </div>

            <ApprovazioniFilters
                value={filters}
                onChange={setFilters}
                matchedCount={turniDelMese.length}
                totalCount={turniDelMeseTutti.length}
                showStatoFilter
            />

            {turniConRelazionePending.length > 0 && (() => {
                const selectedCount = turniConRelazionePending.reduce(
                    (n, c) => n + (selectedRelIds.has(c.rel.id) ? 1 : 0), 0
                );
                const allSelected = selectedCount === turniConRelazionePending.length;
                const someSelected = selectedCount > 0 && !allSelected;
                const toggleAll = () => setSelectedRelIds(prev => {
                    const s = new Set(prev);
                    if (allSelected) {
                        turniConRelazionePending.forEach(c => s.delete(c.rel.id));
                    } else {
                        turniConRelazionePending.forEach(c => s.add(c.rel.id));
                    }
                    return s;
                });
                const approveSelected = async () => {
                    const coppie = turniConRelazionePending.filter(c => selectedRelIds.has(c.rel.id));
                    if (coppie.length === 0) return;
                    if (!window.confirm(`Approvare ${coppie.length} turni selezionati?`)) return;
                    await approveBulkFiduciario(coppie);
                };
                return (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                        <label
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg shadow-sm hover:bg-slate-50 cursor-pointer select-none"
                            title="Seleziona / deseleziona tutti i turni pending visibili"
                        >
                            <input
                                type="checkbox"
                                checked={allSelected}
                                ref={el => { if (el) el.indeterminate = someSelected; }}
                                onChange={toggleAll}
                                className="w-4 h-4 accent-emerald-600 cursor-pointer"
                            />
                            Seleziona tutti
                        </label>
                        {selectedCount > 0 && (
                            <button
                                onClick={approveSelected}
                                disabled={savingBulk}
                                className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-sm disabled:bg-emerald-300 transition-colors"
                            >
                                <ShieldCheck size={14}/> Approva selezionati ({selectedCount})
                            </button>
                        )}
                        <button
                            onClick={() => setBulkApproveOpen(true)}
                            disabled={savingBulk}
                            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50 rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                        >
                            <ShieldCheck size={14}/> Approva tutti ({turniConRelazionePending.length})
                        </button>
                    </div>
                );
            })()}

            {loading && (
                <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" size={24}/></div>
            )}

            {!loading && turniDelMese.length === 0 && (
                <div className="py-12 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200">
                    <FileText size={32} className="text-slate-300 mx-auto mb-2"/>
                    <p className="font-bold text-slate-500 text-sm">
                        Nessun turno nella settimana selezionata
                        {turniDelMeseTutti.length > 0 && ' (con i filtri correnti)'}
                    </p>
                </div>
            )}

            {!loading && turniDelMese.map((t: any) => {
                const isExp = expanded === t.id;
                const orePianificate = oreFra(t.oraInizio, t.oraFine);
                const oreGps = oreFraDate(t.effettivoInizio, t.effettivoFine);
                const isSvolto = !!t.effettivoInizio && !!t.effettivoFine;
                const isInCorso = !!t.effettivoInizio && !t.effettivoFine;
                const rel = reportByTurno[t.id];
                const relApprovata = rel && rel.stato === 'APPROVATO';
                const relPend = rel && (!rel.stato || rel.stato === 'DA_REVISIONARE');
                // Ore cliente = ore pianificate; le extra fuori GPS pesano solo sul costo del collaboratore.
                const oreClienti = orePianificate;
                const ricavoCliente = oreClienti * (tariffaOrariaCliente || 0);
                // Ore extra approvate (override del titolare o valore della relazione), lavorate fuori GPS.
                const oreExtraApprovate = rel
                    ? (Number(editReport[rel.id]?.oreExtraApprovate ?? rel.oreExtraApprovate) || 0)
                    : 0;
                // Ore pagate = override del titolare, altrimenti GPS + extra approvate.
                const orePagate = t.oreCollaboratoreOverride != null
                    ? Number(t.oreCollaboratoreOverride)
                    : oreGps + oreExtraApprovate;
                const tariffaCollab = tariffaCollabMap[t.collaboratore?.id] ?? 0;
                const costoCollab = orePagate * tariffaCollab;
                // Il badge segue la relazione approvata, perché il backend allinea oreApprovate
                // nella stessa transazione. Per turni vecchi senza relazione si usa il flag del turno.
                const oreApprovate = rel ? !!relApprovata : !!t.oreApprovate;

                return (
                    <div key={t.id} className={`bg-white border rounded-xl overflow-hidden shadow-sm ${oreApprovate ? 'border-emerald-200' : 'border-slate-200'}`}>
                        {/* Intestazione turno */}
                        <div className={`px-3 py-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${oreApprovate ? 'bg-emerald-50/20' : 'bg-slate-50/40'}`}
                             onClick={() => setExpanded(isExp ? null : t.id)}>
                            <div className="flex items-center gap-2.5 min-w-0">
                                {relPend && (
                                    <label
                                        onClick={e => e.stopPropagation()}
                                        className="p-1 cursor-pointer shrink-0"
                                        title="Seleziona per approvazione massiva"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedRelIds.has(rel.id)}
                                            onChange={() => setSelectedRelIds(prev => {
                                                const s = new Set(prev);
                                                s.has(rel.id) ? s.delete(rel.id) : s.add(rel.id);
                                                return s;
                                            })}
                                            className="w-4 h-4 accent-emerald-600 cursor-pointer"
                                        />
                                    </label>
                                )}
                                <div className={`p-1.5 rounded-lg shrink-0 ${oreApprovate ? 'bg-emerald-100 text-emerald-600' : isSvolto ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
                                    {oreApprovate ? <ShieldCheck size={14}/> : <Clock size={14}/>}
                                </div>
                                <div className="min-w-0">
                                    <p className="font-bold text-slate-800 text-xs leading-tight mb-1">
                                        {new Date(t.dataTurno).toLocaleDateString('it-IT')} · {t.oraInizio}–{t.oraFine}
                                        <span className="font-normal text-slate-400 ml-1.5">
                                            {t.collaboratore?.name} {t.collaboratore?.surname || ''}
                                        </span>
                                    </p>
                                    {/* Riepilogo: ore GPS, ore cliente, ore extra e km. Extra e km compaiono
                                       solo con una relazione consegnata. */}
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        {/* Timbratura nella finestra di tolleranza del turno (come in ApprovazioneSpeseView). */}
                                        {turnoInRange(t) && (
                                            <span className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-md text-[11px] font-black uppercase">
                                                Turno in range
                                            </span>
                                        )}
                                        <span className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-700 px-1.5 py-0.5 rounded-md text-[11px] font-black">
                                            <MapPin size={11}/> Ore GPS {formatOreMinuti(oreGps)}
                                        </span>
                                        <span className="inline-flex items-center gap-1 bg-sky-50 border border-sky-200 text-sky-700 px-1.5 py-0.5 rounded-md text-[11px] font-black">
                                            <Clock size={11}/> Ore Cliente {formatOreMinuti(orePianificate)}
                                        </span>
                                        {rel && (
                                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-black border ${
                                                rel.autoAziendale
                                                    ? 'bg-purple-50 border-purple-200 text-purple-700'
                                                    : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                            }`}>
                                                <Car size={11}/> {rel.autoAziendale ? 'Km aziend.' : 'Km priv.'} {rel.kmPercorsi || 0}
                                            </span>
                                        )}
                                        {/* Km rettificati dal titolare, validi per il rimborso. */}
                                        {rel && (
                                            <span className="inline-flex items-center gap-1 bg-green-100 border border-green-300 text-green-800 px-1.5 py-0.5 rounded-md text-[11px] font-black">
                                                <Car size={11}/> Km approvati {Number(editReport[rel.id]?.kmApprovati ?? rel.kmApprovati ?? rel.kmPercorsi ?? 0)}
                                            </span>
                                        )}
                                        {rel && (() => {
                                            const oreExtraDich = Number(rel.oreExtra) || 0;
                                            return (
                                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[11px] font-black border ${
                                                    oreExtraDich > 0
                                                        ? 'bg-amber-50 border-amber-200 text-amber-700'
                                                        : 'bg-slate-50 border-slate-200 text-slate-400'
                                                }`}>
                                                    Extra {formatOreMinuti(oreExtraDich, { zero: 'show' })}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                                {/* Ritardo o anticipo oltre 5 minuti: anche un anticipo eccessivo può indicare un problema. */}
                                {(() => {
                                    const r = ritardoMinutiTurno(t);
                                    if (r == null) return null;
                                    // Formato compatto, salta le unità nulle (75m → "1h 15m", 1500m → "1g 1h").
                                    const fmt = (minutiAss: number): string => {
                                        const g = Math.floor(minutiAss / 1440);
                                        const h = Math.floor((minutiAss % 1440) / 60);
                                        const m = minutiAss % 60;
                                        const parts: string[] = [];
                                        if (g > 0) parts.push(`${g}g`);
                                        if (h > 0) parts.push(`${h}h`);
                                        if (m > 0 || parts.length === 0) parts.push(`${m}m`);
                                        return parts.join(' ');
                                    };
                                    if (r > 5) {
                                        return (
                                            <span title={`Inizio in ritardo di ${fmt(r)} sul pianificato`}
                                                  className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-rose-100 text-rose-700 inline-flex items-center gap-0.5">
                                                <AlertTriangle size={10}/> Ritardo +{fmt(r)}
                                            </span>
                                        );
                                    }
                                    if (r < -5) {
                                        return (
                                            <span title={`Inizio in anticipo di ${fmt(Math.abs(r))} sul pianificato`}
                                                  className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-amber-100 text-amber-700">
                                                Anticipo {fmt(Math.abs(r))}
                                            </span>
                                        );
                                    }
                                    return null;
                                })()}
                                {isInCorso ? (
                                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-emerald-100 text-emerald-700 animate-pulse">In servizio</span>
                                ) : isSvolto ? (
                                    oreApprovate ? (
                                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-emerald-100 text-emerald-700">Approvate</span>
                                    ) : (
                                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-amber-100 text-amber-700">Da approvare</span>
                                    )
                                ) : (
                                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-slate-100 text-slate-500">Pianificato</span>
                                )}
                                {isExp ? <ChevronUp size={13} className="text-slate-400"/> : <ChevronDown size={13} className="text-slate-400"/>}
                            </div>
                        </div>

                        {isExp && (
                            <div className="p-3 border-t border-slate-100 space-y-4 bg-slate-50/20">

                                {/* Foto, orario e posizione per ogni ciclo start/stop (es. pausa pranzo).
                                   Per i turni senza t.sessioni si usa una sessione costruita dai campi piatti.
                                   "!= null" sulle coordinate perché 0 è valido. */}
                                {(() => {
                                    const sessioni: any[] = Array.isArray(t.sessioni) && t.sessioni.length > 0
                                        ? collassaSessioniDegenerate(t.sessioni)
                                        : (t.effettivoInizio || t.effettivoFine || t.fotoStartUrl || t.fotoStopUrl
                                            || t.startLat != null || t.endLat != null)
                                            ? [{
                                                id: 0,
                                                startAt: t.effettivoInizio,
                                                endAt: t.effettivoFine,
                                                startLat: t.startLat, startLon: t.startLon,
                                                endLat: t.endLat, endLon: t.endLon,
                                                fotoStartUrl: t.fotoStartUrl,
                                                fotoStopUrl: t.fotoStopUrl,
                                            }]
                                            : [];
                                    if (sessioni.length === 0) return null;
                                    const multi = sessioni.length > 1;
                                    return (
                                        <div>
                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                                                <Camera size={10}/> Foto turno · {sessioni.length} {sessioni.length === 1 ? 'sessione' : 'sessioni'}
                                            </p>
                                            <div className="space-y-3">
                                                {sessioni.map((s: any, idx: number) => (
                                                    <div key={s.id ?? idx} className={multi ? 'bg-white border border-slate-200 rounded-lg p-2.5 shadow-sm' : ''}>
                                                        {multi && (
                                                            <p className="text-[9px] font-bold text-blue-600 uppercase tracking-widest mb-1.5">Sessione {idx + 1}</p>
                                                        )}
                                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                            {/* Inizio */}
                                                            <div className="space-y-1.5">
                                                                {s.fotoStartUrl ? (
                                                                    <div className="relative cursor-pointer" onClick={() => openPreview(s.fotoStartUrl, `Foto Inizio${multi ? ` · Sessione ${idx + 1}` : ' Turno'}`)}>
                                                                        <img src={s.fotoStartUrl} alt={`start-${idx + 1}`} className="w-full h-32 object-cover rounded border border-slate-200 hover:border-blue-400"/>
                                                                        <div className="absolute inset-x-0 bottom-0 bg-emerald-600/90 text-white text-[9px] font-bold text-center py-0.5 rounded-b">▶ INIZIO{multi ? ` S${idx + 1}` : ''}</div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-full h-32 flex flex-col items-center justify-center bg-slate-100 border border-dashed border-slate-300 rounded text-[9px] text-slate-400">
                                                                        <Camera size={16} className="mb-1 opacity-50"/> Foto inizio assente
                                                                    </div>
                                                                )}
                                                                <div className="text-[10px] space-y-0.5">
                                                                    <div className="flex items-center gap-1 font-semibold text-slate-700">
                                                                        <Clock size={11} className="text-emerald-600"/>
                                                                        {s.startAt
                                                                            ? new Date(s.startAt).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })
                                                                            : <span className="italic text-slate-400 font-normal">orario non registrato</span>}
                                                                    </div>
                                                                    {s.startLat != null ? (
                                                                        <a href={`https://maps.google.com/?q=${s.startLat},${s.startLon}`} target="_blank" rel="noreferrer"
                                                                           className="text-emerald-600 hover:text-emerald-700 flex items-center gap-1 font-semibold">
                                                                            <MapPin size={11}/> Posizione inizio
                                                                        </a>
                                                                    ) : (
                                                                        <span className="flex items-center gap-1 italic text-slate-400">
                                                                            <MapPin size={11}/> Posizione non registrata
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {/* Fine */}
                                                            <div className="space-y-1.5">
                                                                {s.fotoStopUrl ? (
                                                                    <div className="relative cursor-pointer" onClick={() => openPreview(s.fotoStopUrl, `Foto Fine${multi ? ` · Sessione ${idx + 1}` : ' Turno'}`)}>
                                                                        <img src={s.fotoStopUrl} alt={`stop-${idx + 1}`} className="w-full h-32 object-cover rounded border border-slate-200 hover:border-blue-400"/>
                                                                        <div className="absolute inset-x-0 bottom-0 bg-rose-600/90 text-white text-[9px] font-bold text-center py-0.5 rounded-b">■ FINE{multi ? ` S${idx + 1}` : ''}</div>
                                                                    </div>
                                                                ) : (
                                                                    <div className="w-full h-32 flex flex-col items-center justify-center bg-slate-100 border border-dashed border-slate-300 rounded text-[9px] text-slate-400">
                                                                        <Camera size={16} className="mb-1 opacity-50"/> {s.endAt || idx < sessioni.length - 1 ? 'Foto fine assente' : (isInCorso ? 'Sessione in corso…' : 'Foto fine assente')}
                                                                    </div>
                                                                )}
                                                                <div className="text-[10px] space-y-0.5">
                                                                    <div className="flex items-center gap-1 font-semibold text-slate-700">
                                                                        <Clock size={11} className="text-rose-600"/>
                                                                        {s.endAt
                                                                            ? new Date(s.endAt).toLocaleString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })
                                                                            : <span className="italic text-slate-400 font-normal">{isInCorso && idx === sessioni.length - 1 ? 'in corso…' : 'non terminato'}</span>}
                                                                    </div>
                                                                    {s.endLat != null ? (
                                                                        <a href={`https://maps.google.com/?q=${s.endLat},${s.endLon}`} target="_blank" rel="noreferrer"
                                                                           className="text-rose-600 hover:text-rose-700 flex items-center gap-1 font-semibold">
                                                                            <MapPin size={11}/> Posizione fine
                                                                        </a>
                                                                    ) : (
                                                                        <span className="flex items-center gap-1 italic text-slate-400">
                                                                            <MapPin size={11}/> Posizione non registrata
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* Revisione ore / override / approvazione */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2.5">
                                    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                        <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Ore Cliente</p>
                                        <p className="font-black text-slate-700 text-base">{formatOreMinuti(orePianificate)}</p>
                                        <p className="text-[9px] text-slate-400 mt-1">Pianificate del turno</p>
                                        {/* A forfait il ricavo è fisso da contratto: la quota per turno non è significativa. */}
                                        {isForfait ? (
                                            <p className="text-[9px] text-slate-400 mt-1">Ricavo cliente: forfait contratto</p>
                                        ) : (
                                            <p className="text-[9px] text-slate-400 mt-1">Ricavo cliente: € {ricavoCliente.toFixed(2)}</p>
                                        )}
                                    </div>
                                    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                        <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Ore GPS</p>
                                        <p className="font-black text-slate-700 text-base">{oreGps > 0 ? formatOreMinuti(oreGps) : '0 ore'}</p>
                                        {isInCorso && <p className="text-[9px] text-emerald-600 mt-1 animate-pulse">In corso…</p>}
                                    </div>
                                    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                        <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Ore Extra</p>
                                        <p className="font-black text-slate-700 text-base">{oreExtraApprovate > 0 ? formatOreMinuti(oreExtraApprovate) : '0 ore'}</p>
                                        <p className="text-[9px] text-slate-400 mt-1">Dichiarate dal collaboratore</p>
                                    </div>
                                    <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                        <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Ore Pagate</p>
                                        {(isInCorso || isSvolto) && rel && relPend ? (() => {
                                            // Valore dell'input = override del turno se presente, altrimenti GPS + extra.
                                            // "Ripristina" riporta al default, utile per eliminare override non più validi.
                                            const defaultDec = oreGps + oreExtraApprovate;
                                            const defDec = t.oreCollaboratoreOverride != null ? Number(t.oreCollaboratoreOverride) : defaultDec;
                                            const oreCorrenti = editReport[rel.id]?.oreCollab != null ? Number(editReport[rel.id].oreCollab) : defDec;
                                            // "Ripristina" compare se il campo è stato modificato o l'override differisce dal default.
                                            const showReset =
                                                editReport[rel.id]?.oreCollab !== undefined ||
                                                Math.abs(defDec - defaultDec) > 0.001;
                                            return (
                                                <div className="bg-blue-50 border border-blue-100 rounded-lg p-2">
                                                    <div className="flex items-center gap-1">
                                                        {/* Un solo campo in ore decimali (es. 1.5), come in Investigazioni. */}
                                                        <NumberInput min={0} step="0.5" maxDecimals={2}
                                                            className="w-16 p-1 text-center font-black text-xs bg-white border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400"
                                                            value={oreCorrenti}
                                                            onFocus={e => e.currentTarget.select()}
                                                            onChange={e => setRelField(rel.id, 'oreCollab', e.target.value === '' ? 0 : Number(e.target.value))}/>
                                                        <span className="text-[10px] font-bold text-blue-500">h</span>
                                                        {showReset && (
                                                            <button
                                                                title="Ripristina default GPS + extra"
                                                                onClick={() => setEditReport(prev => ({
                                                                    ...prev,
                                                                    [rel.id]: { ...prev[rel.id], oreCollab: Math.round(defaultDec * 100) / 100 },
                                                                }))}
                                                                className="p-0.5 text-rose-400 border border-rose-200 rounded bg-white hover:bg-rose-50"
                                                            >
                                                                <XCircle size={11}/>
                                                            </button>
                                                        )}
                                                    </div>
                                                    <p className="text-[9px] text-slate-500 mt-1 font-semibold">
                                                        = {formatOreMinuti(oreCorrenti, { zero: 'show' })}
                                                    </p>
                                                    <p className="text-[8px] text-slate-400 mt-0.5">
                                                        Default: GPS {oreGps > 0 ? formatOreMinuti(oreGps) : '0'}
                                                        {oreExtraApprovate > 0 && ` + extra ${formatOreMinuti(oreExtraApprovate)}`}
                                                    </p>
                                                </div>
                                            );
                                        })() : (
                                            // Turno non svolto o relazione già approvata: sola lettura.
                                            <div className="flex items-center justify-between w-full gap-2 px-2 py-1 -mx-2">
                                                <span className={`font-black text-base ${isInCorso || isSvolto ? 'text-slate-700' : 'text-slate-400'}`}>{formatOreMinuti(orePagate)}</span>
                                                {!isSvolto && !isInCorso && (
                                                    <span className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">Pianificato</span>
                                                )}
                                            </div>
                                        )}
                                        {(isSvolto || isInCorso) && oreExtraApprovate > 0 && t.oreCollaboratoreOverride == null && !relPend && (
                                            <p className="text-[9px] text-slate-400 mt-1">{formatOreMinuti(oreGps)} GPS + {formatOreMinuti(oreExtraApprovate)} extra</p>
                                        )}
                                    </div>
                                </div>

                                {/* Le ore si approvano insieme alla relazione (il backend allinea oreApprovate):
                                   qui si mostra solo lo stato. */}
                                {isSvolto && oreApprovate && (
                                    <div className="border-t border-slate-200 pt-3 flex items-center gap-2 bg-emerald-50/40 -mx-3 px-3 py-2 rounded">
                                        <ShieldCheck size={14} className="text-emerald-600 shrink-0"/>
                                        <span className="text-[11px] font-semibold text-emerald-700">
                                            Ore turno approvate insieme alla relazione.
                                        </span>
                                    </div>
                                )}

                                {/* Relazione giornaliera (se presente) */}
                                {rel ? (
                                    <div className="border-t border-slate-200 pt-3 space-y-3">
                                        <div className="flex items-center justify-between">
                                            <p className="text-[10px] font-bold text-slate-600 uppercase tracking-widest flex items-center gap-1.5">
                                                <FileText size={11} className="text-blue-400"/> Relazione di servizio
                                            </p>
                                            <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded ${relPend ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                {relPend ? 'Da approvare' : 'Approvata'}
                                            </span>
                                        </div>

                                        {/* Testo relazione */}
                                        <div className="bg-white border border-slate-200 p-2.5 rounded-lg text-xs text-slate-700 leading-relaxed whitespace-pre-wrap shadow-sm">
                                            {rel.testoRelazione || <span className="italic text-slate-400">Nessun testo compilato.</span>}
                                        </div>

                                        {/* Ore extra + Km */}
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                                            <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                                <p className="text-[9px] font-bold text-slate-400 uppercase mb-2">Ore extra dichiarate</p>
                                                <p className="font-black text-slate-700 text-base">{formatOreMinuti(Number(rel.oreExtra || 0))}</p>
                                                <p className="text-[9px] text-slate-400 mt-1">
                                                    Sommate alle ore GPS nel calcolo delle ore pagate. Per rettificare,
                                                    usa il campo <span className="font-semibold text-slate-500">Ore pagate</span> sopra.
                                                </p>
                                            </div>
                                            <div className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
                                                <p className="text-[9px] font-bold text-slate-400 uppercase mb-2 flex items-center gap-1"><Car size={10}/> Km</p>
                                                <div className="flex justify-between text-[10px] mb-1">
                                                    <span className="text-slate-400">Dichiarati:</span>
                                                    <span className="font-black text-slate-700">{rel.kmPercorsi || 0} km</span>
                                                </div>
                                                {/* Tipo di veicolo dichiarato nel report di fine turno, per distinguere i km
                                                   da rimborsare da quelli aziendali. Il toggle sotto permette di rettificarlo. */}
                                                <div className="flex justify-between text-[10px] mb-2">
                                                    <span className="text-slate-400">Veicolo:</span>
                                                    <span className={`font-black ${rel.autoAziendale ? 'text-purple-600' : 'text-slate-700'}`}>
                                                        {rel.autoAziendale ? 'Auto aziendale' : 'Auto privata'}
                                                    </span>
                                                </div>
                                                {relPend ? (
                                                    <div className="bg-blue-50 border border-blue-100 rounded-lg p-2 space-y-1.5">
                                                        <p className="text-[8px] font-bold text-blue-700 uppercase">Km approvati</p>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => setRelField(rel.id, 'kmApprovati', 0)} className="p-1 bg-white text-rose-400 border border-rose-200 rounded hover:bg-rose-50"><XCircle size={12}/></button>
                                                            <NumberInput min={0}
                                                                className="flex-1 p-1 text-center font-black text-xs bg-white border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400"
                                                                value={editReport[rel.id]?.kmApprovati ?? 0}
                                                                onChange={e => setRelField(rel.id, 'kmApprovati', e.target.value === '' ? '' : Number(e.target.value))}/>
                                                            <span className="text-[10px] font-bold text-blue-500">km</span>
                                                        </div>
                                                        {/* Auto aziendale: il titolare conferma o rettifica. Si salva subito con
                                                           updateAutoAziendale, come in TabReport, per restare allineati al DB. */}
                                                        <AutoAziendaleSelector
                                                            size="xs"
                                                            value={!!editReport[rel.id]?.autoAziendale}
                                                            onChange={async nuovo => {
                                                                setRelField(rel.id, 'autoAziendale', nuovo);
                                                                setReports(prev => prev.map(r => r.id === rel.id ? { ...r, autoAziendale: nuovo } : r));
                                                                try {
                                                                    await RelazioneGiornalieraService.updateAutoAziendale(rel.id, nuovo);
                                                                } catch {
                                                                    // Rollback ottimistico su errore
                                                                    setRelField(rel.id, 'autoAziendale', !nuovo);
                                                                    setReports(prev => prev.map(r => r.id === rel.id ? { ...r, autoAziendale: !nuovo } : r));
                                                                    addToast('Errore aggiornamento auto', 'error');
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className="bg-emerald-50 border border-emerald-100 rounded p-1.5 space-y-1">
                                                        <div className="flex justify-between items-center">
                                                            <span className="text-[8px] font-bold text-emerald-600 uppercase">Approvati:</span>
                                                            <span className="font-black text-emerald-700 text-xs">{rel.kmApprovati ?? 0} km</span>
                                                        </div>
                                                        {rel.autoAziendale && (
                                                            <div className="flex items-center gap-1 text-[8px] font-bold text-purple-600">
                                                                <Car size={10}/> Auto aziendale
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Spese */}
                                        {(rel.speseEspanso?.length > 0 || rel.spese?.length > 0) && (
                                            <div>
                                                <p className="text-[9px] font-bold text-slate-600 uppercase tracking-widest mb-1.5 flex items-center gap-1.5"><Receipt size={11} className="text-blue-400"/> Spese</p>
                                                <div className="space-y-1.5">
                                                    {(editReport[rel.id]?.spese || []).map((s: any) => (
                                                        <div key={s.id} className={`flex items-center gap-2 p-2 rounded-lg border text-[10px] ${s.anticipatoAzienda ? 'bg-purple-50/50 border-purple-200' : 'bg-white border-slate-200'}`}>
                                                            <div className={`p-1 rounded ${s.anticipatoAzienda ? 'bg-purple-100 text-purple-600' : 'bg-slate-100 text-slate-400'}`}>
                                                                {s.anticipatoAzienda ? <CreditCard size={11}/> : <Receipt size={11}/>}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    <span className="font-bold text-slate-700">{s.tipo}</span>
                                                                    {s.anticipatoAzienda && (
                                                                        <span className="px-1 py-0.5 bg-purple-100 text-purple-700 text-[8px] font-black uppercase rounded-full border border-purple-200">Carta az.</span>
                                                                    )}
                                                                    <span className={`${!s.anticipatoAzienda && Number(s.importoApprovato) !== Number(s.importo) ? 'line-through text-rose-400' : 'text-slate-400'}`}>€{Number(s.importo || 0).toFixed(2)}</span>
                                                                </div>
                                                            </div>
                                                            {/* Scontrino aperto nella stessa lightbox delle foto. */}
                                                            {s.scontrinoUrl && (
                                                                <button type="button"
                                                                        onClick={() => { setPreviewUrl(s.scontrinoUrl); setPreviewLabel(`Scontrino${s.tipo ? ` - ${s.tipo}` : ''}`); }}
                                                                        title="Apri scontrino"
                                                                        className="text-blue-400 hover:text-blue-600 shrink-0">
                                                                    <ExternalLink size={11}/>
                                                                </button>
                                                            )}
                                                            {relPend ? (
                                                                <div className="flex items-center gap-1 shrink-0">
                                                                    {/* Carta aziendale/propria rettificabile dal titolare: se aziendale il rimborso è 0. */}
                                                                    <button type="button"
                                                                            onClick={() => toggleSpesaAnticipata(rel.id, s.id)}
                                                                            title={s.anticipatoAzienda ? 'Spesa anticipata dall\'azienda: nessun rimborso al collaboratore' : 'Spesa di tasca del collaboratore: importo da rimborsare'}
                                                                            className={`px-1.5 py-1 rounded text-[8px] font-bold uppercase flex items-center gap-1 border transition-colors ${
                                                                                s.anticipatoAzienda
                                                                                    ? 'bg-purple-50 text-purple-600 border-purple-200 hover:bg-purple-100'
                                                                                    : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'
                                                                            }`}>
                                                                        <CreditCard size={9}/>
                                                                        <span>{s.anticipatoAzienda ? 'Carta az.' : 'Carta propria'}</span>
                                                                        {s.anticipatoAzienda
                                                                            ? <ToggleRight size={12} className="text-purple-500"/>
                                                                            : <ToggleLeft size={12} className="text-slate-300"/>}
                                                                    </button>
                                                                    {s.anticipatoAzienda ? (
                                                                        <div className="flex items-center gap-1 shrink-0" title="Importo pagato con carta aziendale">
                                                                            <span className="text-[8px] font-black text-purple-600 uppercase">Carta €</span>
                                                                            <NumberInput min={0} step="0.01"
                                                                                className="w-16 p-1 text-center font-black text-[10px] bg-white border border-purple-200 rounded outline-none focus:ring-1 ring-purple-400"
                                                                                value={s.importoCarta ?? s.importo}
                                                                                onChange={e => setRelSpesa(rel.id, s.id, e.target.value === '' ? '' : Number(e.target.value), 'importoCarta')}/>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            <span className="font-bold text-blue-600 text-[9px]">€</span>
                                                                            <NumberInput min={0} step="0.01"
                                                                                className="w-16 p-1 text-center font-black text-[10px] bg-white border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400"
                                                                                value={s.importoApprovato}
                                                                                onChange={e => setRelSpesa(rel.id, s.id, e.target.value === '' ? '' : Number(e.target.value))}/>
                                                                            <button onClick={() => setRelSpesa(rel.id, s.id, s.importo)} className="p-0.5 text-rose-400 border border-rose-200 rounded bg-white hover:bg-rose-50"><XCircle size={11}/></button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            ) : s.anticipatoAzienda ? (
                                                                <span className="text-[8px] font-black text-purple-600 uppercase shrink-0">No rimborso</span>
                                                            ) : (
                                                                <span className="font-black text-emerald-700 text-xs shrink-0">€{Number(s.importoApprovato || 0).toFixed(2)}</span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Buono pasto, solo se il collaboratore ha soglia e importo configurati per i fiduciari.
                                           Si aggiorna con le ore modificate inline. */}
                                        {relPend && (
                                            <BuonoPastoAutoCard
                                                oreLavorate={
                                                    // Usa le ore pagate che il titolare sta scrivendo (es. 20h → 4 buoni);
                                                    // se il campo non è stato toccato, le ore effettive del turno.
                                                    (editReport[rel.id]?.oreCollab != null && editReport[rel.id]?.oreCollab !== '')
                                                        ? (Number(editReport[rel.id].oreCollab) || 0)
                                                        : oreEffettiveTurnoForBuonoPasto(t, Number(editReport[rel.id]?.oreExtraApprovate) || 0)
                                                }
                                                sogliaOre={rel.collaboratore?.sogliaBuonoPastoTurniOre}
                                                importo={rel.collaboratore?.importoBuonoPastoTurni}
                                                applica={isBpAttivo(rel.id)}
                                                onChangeApplica={v => setApplicaBpByRel(prev => ({ ...prev, [rel.id]: v }))}
                                            />
                                        )}

                                        {/* Approva / annulla relazione */}
                                        <div className="flex justify-end pt-2.5">
                                            {relPend ? (
                                                <button onClick={() => approvaRelazione(rel, t)} disabled={savingRelId === rel.id}
                                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-1.5 text-xs shadow-md">
                                                    {savingRelId === rel.id ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle2 size={13}/>}
                                                    Approva relazione e invia a contabilità
                                                </button>
                                            ) : (
                                                <button onClick={() => annullaRelazione(rel)} disabled={annullandoRelId === rel.id}
                                                        className="bg-white hover:bg-rose-50 text-rose-600 border border-rose-200 px-3 py-1.5 rounded-lg font-bold flex items-center gap-1.5 text-xs">
                                                    {annullandoRelId === rel.id ? <Loader2 size={12} className="animate-spin"/> : <XCircle size={12}/>}
                                                    Annulla approvazione
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    isSvolto && (
                                        <div className="border-t border-slate-200 pt-3 flex items-center gap-2 bg-amber-50/40 -mx-3 -mb-3 px-3 py-2.5">
                                            <AlertTriangle size={14} className="text-amber-500 shrink-0"/>
                                            <p className="text-[11px] text-amber-700">
                                                In attesa che il collaboratore invii il report di fine turno (km, spese, note). L'approvazione delle ore avviene insieme alla relazione.
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Anteprima per foto e scontrini. PDF/video riconosciuti dall'estensione
               dell'URL, senza query string. */}
            {previewUrl && (() => {
                const clean = previewUrl.split('?')[0].toLowerCase();
                const isPdf = clean.endsWith('.pdf');
                const isVideo = /\.(mp4|mov|webm|m4v|mts|m2ts|mkv)$/.test(clean);
                return (
                    <Modal title={previewLabel} onClose={() => { setPreviewUrl(null); setPreviewLabel(''); }}>
                        <div className="flex flex-col items-center bg-slate-900 -mx-5 -mb-5 p-4 rounded-b-xl">
                            {isVideo ? (
                                <video src={previewUrl} controls className="w-full rounded-lg max-h-[70vh] bg-black"/>
                            ) : isPdf ? (
                                <iframe src={previewUrl} title={previewLabel || 'PDF'} className="w-full h-[70vh] rounded-lg bg-white"/>
                            ) : (
                                <img src={previewUrl} alt={previewLabel} className="w-full rounded-lg max-h-[70vh] object-contain"/>
                            )}
                        </div>
                    </Modal>
                );
            })()}

            {bulkApproveOpen && (
                <BulkApproveModal
                    count={turniConRelazionePending.length}
                    filters={filters}
                    saving={savingBulk}
                    label="report"
                    onCancel={() => { if (!savingBulk) setBulkApproveOpen(false); }}
                    onConfirm={() => approveBulkFiduciario(turniConRelazionePending)}
                />
            )}
        </div>
    );
};

export default TabReportServizio;
